import IBaseModel from "../base/base-model";
import { ILocation } from "../store/response";
import { IOrderCustomerSatisfaction } from "../customer-satisfaction/response";
import { IProductsOrder } from "./products-order/response";

// Simple location for location change feature
export interface ISimpleLocation {
  lat: number;
  lng: number;
}

// Browsing store for location change feature
export interface IBrowsingStore {
  id: number;
  name: string;
}

// Trip status enum for order protection
export enum TripStatus {
  PENDING = 1,
  ACTIVE = 2,
  COMPLETED = 3,
  CANCELLED = 4,
}

// Delivery mode enum
export enum OrderDeliveryMode {
  LEGACY = 1,
  TRIP_BASED = 2,
}

export type CustomerSatisfactionStatusSummaryKey =
  | "satisfied"
  | "unsatisfied"
  | "no_response"
  | "undefined";

export interface ICustomerSatisfactionStatusSummary {
  value: 1 | 2 | 3 | null;
  key: CustomerSatisfactionStatusSummaryKey | string;
  label: string;
  determinedAt?: string | null;
}

export type OrderOperationTimingPhaseKey =
  | "storeAcceptance"
  | "preparing"
  | "customerAcceptance"
  | "accountingFinalization"
  | "driverSearchToPickup"
  | "delivery";

export interface IOrderOperationDuration {
  totalSeconds: number;
  minutes: number;
  seconds: number;
}

export interface IOrderOperationTimingPhase {
  key: OrderOperationTimingPhaseKey | string;
  startedAt?: string | null;
  endedAt?: string | null;
  durationSeconds?: number | null;
  duration?: IOrderOperationDuration | null;
  elapsedSeconds?: number | null;
  elapsedDuration?: IOrderOperationDuration | null;
  effectiveSeconds?: number | null;
  effectiveDuration?: IOrderOperationDuration | null;
  isActive?: boolean;
}

export interface IOrderOperationTimingsDataQuality {
  missingCycleStart?: boolean;
  missingCompletedAt?: boolean;
  repeatedStatuses?: Array<string | number | Record<string, any>>;
  stageSumGreaterThanTotal?: boolean;
  [key: string]: any;
}

export interface IOrderOperationTimings {
  cycleStartedAt?: string | null;
  completedAt?: string | null;
  finishedAt?: string | null;
  measuredUntilAt?: string | null;
  currentPhase?: OrderOperationTimingPhaseKey | string | null;
  currentPhaseElapsedSeconds?: number | null;
  currentPhaseElapsedDuration?: IOrderOperationDuration | null;
  totalCycleSeconds?: number | null;
  totalCycleDuration?: IOrderOperationDuration | null;
  totalCompletedSeconds?: number | null;
  totalCompletedDuration?: IOrderOperationDuration | null;
  storeAcceptanceSeconds?: number | null;
  storeAcceptanceDuration?: IOrderOperationDuration | null;
  preparingSeconds?: number | null;
  preparingDuration?: IOrderOperationDuration | null;
  customerAcceptanceSeconds?: number | null;
  customerAcceptanceDuration?: IOrderOperationDuration | null;
  accountingFinalizationSeconds?: number | null;
  accountingFinalizationDuration?: IOrderOperationDuration | null;
  driverSearchToPickupSeconds?: number | null;
  driverSearchToPickupDuration?: IOrderOperationDuration | null;
  deliverySeconds?: number | null;
  deliveryDuration?: IOrderOperationDuration | null;
  unattributedSeconds?: number | null;
  unattributedDuration?: IOrderOperationDuration | null;
  stageCoverageRate?: number | null;
  stageSumGreaterThanTotal?: boolean;
  isCompleted?: boolean;
  isTerminal?: boolean;
  phases?: Partial<Record<OrderOperationTimingPhaseKey, IOrderOperationTimingPhase>> &
    Record<string, IOrderOperationTimingPhase | undefined>;
  dataQuality?: IOrderOperationTimingsDataQuality;
}

export interface IOrderShort {
  id: number;
  status: number;
  createdAt: string;
  store: any;
  // Trip information fields
  tripId?: number | null;
  tripNumber?: string | null;
  tripStatus?: TripStatus | null;
  // Delivery mode
  deliveryMode?: OrderDeliveryMode;
  // Location change fields
  locationChanged?: boolean;
  currentLocation?: ISimpleLocation | null;
  browsingStore?: IBrowsingStore | null;
  deliveryStore?: IBrowsingStore | null;
  browsingTotalPrice?: number | null;
  deliveryTotalPrice?: number | null;
  priceDifference?: number | null;
}

export interface IOrder {
  id: number;
  client?: Client | null;
  driver: any;
  type: number;
  store: any;
  investor: any;
  status: number;
  finalPrice: number;
  isEmergency: boolean;
  isRecipe: boolean;
  isExpressDelivery: boolean;
  isSubscriptionCoupon?: boolean;
  client_note: any;
  note: string;
  createdAt: string;
  orderSchedulingType: number;
  paymentReceivedBy?: User;
  paymentReceivedAt?: string;
  accountingFinishedBy?: User;
  accountingFinishedAt?: string | null;
  location?: ILocation;
  // Trip information fields
  tripId?: number | null;
  tripNumber?: string | null;
  tripStatus?: TripStatus | null;
  // Delivery mode
  deliveryMode?: OrderDeliveryMode;
  // Location change fields
  locationChanged?: boolean;
  currentLocation?: ISimpleLocation | null;
  browsingStore?: IBrowsingStore | null;
  deliveryStore?: IBrowsingStore | null;
  browsingTotalPrice?: number | null;
  deliveryTotalPrice?: number | null;
  priceDifference?: number | null;
  customerSatisfaction?: IOrderCustomerSatisfaction | null;
  customerSatisfactionStatus?: ICustomerSatisfactionStatusSummary | null;
  operationTimings?: IOrderOperationTimings | null;
}

export interface IRatedOrder {
  id: number;
  client?: Client | null;
  type: number;
  status: number;
  finalPrice: number;
  totalPriceOnClient: number;
  rating: any;
  clientRateNote: string;
  createdAt: string;
  finishDatetime: string;
  // Trip information fields
  tripId?: number | null;
  tripNumber?: string | null;
  tripStatus?: TripStatus | null;
  // Delivery mode
  deliveryMode?: OrderDeliveryMode;
  // Location change fields
  locationChanged?: boolean;
  currentLocation?: ISimpleLocation | null;
  browsingStore?: IBrowsingStore | null;
  deliveryStore?: IBrowsingStore | null;
  browsingTotalPrice?: number | null;
  deliveryTotalPrice?: number | null;
  priceDifference?: number | null;
}

export interface IOrderDetails extends IBaseModel {
  id: number;
  number: string;
  client?: Client | null;
  driver: any;
  type: number;
  store: any;
  investor: any;
  status: number;
  finalPrice: number;
  isEmergency: boolean;
  orderBroadcastArea: boolean;
  orderBroadcastCity: boolean;
  productBroadcastArea: boolean;
  productBroadcastCity: boolean;
  isRecipe: boolean;
  StoreHaveRecipe: any;
  recipes: any[];
  recipeAsText: any;
  notes: any[];
  clientNote: any;
  distance: any;
  totalPrice: number;
  totalPriceOnClient: number;
  productYield: number;
  priceDeliveryService: number;
  priceDeliveryServiceOnClient: number;
  priceDeliveryYield: number;
  discountCode: any;
  discountCodeValueForDelivery: number;
  discountCodeValueForProduct: number;
  rating: any;
  contact: boolean;
  ringingBell: boolean;
  clientNoteDelivery: any;
  note: string;
  orderProducts: IProductsOrder[];
  selectedStore: boolean;
  reasonCancel: any;
  clientRateNote: string;
  finishDatetime: Date;
  startDatetimeDriver: Date;
  clientAddress: string;
  orderSchedulingType: number;
  scheduleTime: Date;
  location: ILocation
  isExpressDelivery: boolean;
  isSubscriptionCoupon?: boolean;
  quantityProcessed: boolean;
  paymentReceivedBy?: User;
  paymentReceivedAt?: string;
  accountingFinishedBy?: User;
  accountingFinishedAt?: string | null;
  accountingFinishNote?: string | null;
  canMarkAccountingFinished?: boolean;
  customerSatisfaction?: IOrderCustomerSatisfaction | null;
  customerSatisfactionStatus?: ICustomerSatisfactionStatusSummary | null;
  operationTimings?: IOrderOperationTimings | null;
  // Trip protection fields
  tripId?: number | null;
  tripNumber?: string | null;
  tripStatus?: TripStatus | null;
  // Delivery mode
  deliveryMode?: OrderDeliveryMode;
  // Location change fields
  locationChanged?: boolean;
  currentLocation?: ISimpleLocation | null;
  browsingStore?: IBrowsingStore | null;
  deliveryStore?: IBrowsingStore | null;
  browsingTotalPrice?: number | null;
  deliveryTotalPrice?: number | null;
  priceDifference?: number | null;
}

export interface Client {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  mobile?: string | null;
  cleanMobile?: string | null;
  acceptedOrdersCount?: number
  completedOrdersCount?: number
  lastCompletedOrderDate?: string
}

export interface User {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
}
